import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  TrendingUp,
  Plus,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, api } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="status-pending text-xs">Pending</Badge>;
      case "in_progress":
        return <Badge className="status-in-progress text-xs">In Progress</Badge>;
      case "completed":
        return <Badge className="status-completed text-xs">Completed</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="dashboard-welcome">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-500 font-body mt-2">
              Here's an overview of your tax practice
            </p>
          </div>
          <Button 
            onClick={() => navigate("/clients")}
            data-testid="add-client-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Client
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm card-hover animate-fade-in" data-testid="stat-total-clients">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Total Clients</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-2">{stats?.total_clients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm card-hover animate-fade-in-delay-1" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Pending Review</p>
                  <p className="text-3xl font-heading font-bold text-amber-600 mt-2">{stats?.pending_clients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-sm flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm card-hover animate-fade-in-delay-2" data-testid="stat-completed">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Completed</p>
                  <p className="text-3xl font-heading font-bold text-emerald-600 mt-2">{stats?.completed_clients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-sm flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm card-hover animate-fade-in-delay-3" data-testid="stat-analyses">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-body text-slate-500 uppercase tracking-wider">Analyses Done</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-2">{stats?.total_analyses || 0}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Clients */}
          <Card className="lg:col-span-2 bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="recent-clients-card">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading font-semibold text-slate-900">Recent Clients</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate("/clients")}
                  className="text-slate-600 hover:text-slate-900 font-body"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {stats?.recent_clients?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {stats.recent_clients.map((client) => (
                    <div 
                      key={client.client_id}
                      className="p-4 hover:bg-slate-50/50 cursor-pointer transition-colors duration-200"
                      onClick={() => navigate(`/clients/${client.client_id}`)}
                      data-testid={`recent-client-${client.client_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                            <span className="text-sm font-heading font-semibold text-slate-600">
                              {client.name?.charAt(0)?.toUpperCase() || "C"}
                            </span>
                          </div>
                          <div>
                            <p className="font-body font-medium text-slate-900">{client.name}</p>
                            <p className="text-sm text-slate-500 font-body">Tax Year {client.tax_year}</p>
                          </div>
                        </div>
                        {getStatusBadge(client.status)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body">No clients yet</p>
                  <Button 
                    onClick={() => navigate("/clients")}
                    className="mt-4 bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                  >
                    Add Your First Client
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Analyses */}
          <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="recent-analyses-card">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
              <CardTitle className="text-lg font-heading font-semibold text-slate-900">Recent Analyses</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {stats?.recent_analyses?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {stats.recent_analyses.map((analysis) => (
                    <div 
                      key={analysis.analysis_id}
                      className="p-4 hover:bg-slate-50/50 cursor-pointer transition-colors duration-200"
                      onClick={() => navigate(`/clients/${analysis.client_id}`)}
                      data-testid={`recent-analysis-${analysis.analysis_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-50 rounded-sm flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-body text-sm font-medium text-slate-900 truncate">
                            Analysis #{analysis.analysis_id.slice(-6)}
                          </p>
                          <p className="text-xs text-slate-500 font-body">
                            Tax Year {analysis.tax_year}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-sm">No analyses yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="quick-actions-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="h-auto p-4 flex flex-col items-start gap-2 border-slate-200 hover:bg-slate-50 rounded-md"
                onClick={() => navigate("/clients")}
                data-testid="quick-action-new-client"
              >
                <Users className="w-5 h-5 text-slate-600" />
                <div className="text-left">
                  <p className="font-body font-medium text-slate-900">Add New Client</p>
                  <p className="text-xs text-slate-500 font-body">Create a new client profile</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto p-4 flex flex-col items-start gap-2 border-slate-200 hover:bg-slate-50 rounded-md"
                onClick={() => navigate("/clients")}
                data-testid="quick-action-upload"
              >
                <FileText className="w-5 h-5 text-slate-600" />
                <div className="text-left">
                  <p className="font-body font-medium text-slate-900">Upload Documents</p>
                  <p className="text-xs text-slate-500 font-body">Add tax returns to analyze</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto p-4 flex flex-col items-start gap-2 border-slate-200 hover:bg-slate-50 rounded-md"
                onClick={() => navigate("/team")}
                data-testid="quick-action-team"
              >
                <AlertCircle className="w-5 h-5 text-slate-600" />
                <div className="text-left">
                  <p className="font-body font-medium text-slate-900">Manage Team</p>
                  <p className="text-xs text-slate-500 font-body">View and manage team members</p>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
